import React, { useEffect, useState } from "react";
import { PageHeader } from "./Pengumuman";
import { Leaf, Trash2, User as UserIcon, Plus, Edit3, X, Camera, ImagePlus, CheckCircle2, Loader2, CalendarDays, ClipboardList } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { petugasLayananAPI, absensiLayananAPI, jadwalTamanAPI, API } from "../lib/api";
import { useApp } from "../context/AppContext";
import JadwalTaman from "./JadwalTaman";
import LaporanKegiatanDialog from "../components/LaporanKegiatanDialog";

const emptyPetugas = { nama: "", jenis: "tukang_taman", kontak: "", foto: "", jadwal: "", catatan: "", active: true };
const emptyAbsen = { petugas_id: "", tanggal: new Date().toISOString().slice(0, 10), jam: "", status: "hadir", foto: [], catatan: "", jadwal_id: "" };

const LayananLingkungan = () => {
  const { user } = useApp();
  const canEdit = user?.role === "admin" || user?.role === "super_admin";
  const [jenis, setJenis] = useState("tukang_taman");
  const [petugas, setPetugas] = useState([]);
  const [absensi, setAbsensi] = useState([]);
  const [jadwalTamanByPetugas, setJadwalTamanByPetugas] = useState({});
  const [pOpen, setPOpen] = useState(false);
  const [pForm, setPForm] = useState(emptyPetugas);
  const [pEditing, setPEditing] = useState(null);
  const [aOpen, setAOpen] = useState(false);
  const [aForm, setAForm] = useState(emptyAbsen);
  const [uploading, setUploading] = useState(false);
  const [lapPrefill, setLapPrefill] = useState(null);

  const load = () => {
    petugasLayananAPI.list(jenis).then(async (list) => {
      setPetugas(list);
      // Preload jadwal for each tukang taman (for absensi linking)
      if (jenis === "tukang_taman") {
        const map = {};
        for (const p of list) {
          try { map[p.id] = await jadwalTamanAPI.list(p.id); } catch { map[p.id] = []; }
        }
        setJadwalTamanByPetugas(map);
      } else {
        setJadwalTamanByPetugas({});
      }
    }).catch(() => {});
    absensiLayananAPI.list({ jenis, limit: 100 }).then(setAbsensi).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jenis]);

  const uploadOne = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await API.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data.url;
  };

  const savePetugas = async () => {
    if (!pForm.nama) { toast.error("Nama wajib"); return; }
    try {
      if (pEditing) { await petugasLayananAPI.update(pEditing.id, pForm); toast.success("Diperbarui"); }
      else { await petugasLayananAPI.create({ ...pForm, jenis }); toast.success("Ditambahkan"); }
      setPOpen(false); setPEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const saveAbsen = async () => {
    if (!aForm.petugas_id || !aForm.tanggal) { toast.error("Pilih petugas & tanggal"); return; }
    try {
      await absensiLayananAPI.create(aForm);
      // Kalau absen dikaitkan ke hari jadwal & status hadir → tandai jadwal jadi selesai
      if (aForm.jadwal_id && aForm.status === "hadir") {
        try { await jadwalTamanAPI.update(aForm.jadwal_id, { status: "selesai" }); }
        catch { /* soft-fail */ }
      }
      toast.success("Absen tersimpan");
      setAOpen(false); setAForm({ ...emptyAbsen });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const uploadPetugasFoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Maks 5MB"); return; }
    setUploading(true);
    try { const url = await uploadOne(f); setPForm((p) => ({ ...p, foto: url })); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload gagal"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const uploadAbsenFoto = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name} > 5MB`); continue; }
        urls.push(await uploadOne(f));
      }
      setAForm((a) => ({ ...a, foto: [...(a.foto || []), ...urls] }));
    } catch (err) { toast.error(err.response?.data?.detail || "Upload gagal"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div>
      <PageHeader title="Petugas Layanan Lingkungan" subtitle="Tukang Taman & Petugas Sampah — identitas, jadwal, dan absensi" icon={Leaf} />

      <Tabs value={jenis} onValueChange={setJenis}>
        <TabsList>
          <TabsTrigger value="tukang_taman" data-testid="tab-tukang-taman"><Leaf className="h-4 w-4 mr-1" />Tukang Taman</TabsTrigger>
          <TabsTrigger value="petugas_sampah" data-testid="tab-petugas-sampah"><Trash2 className="h-4 w-4 mr-1" />Petugas Sampah</TabsTrigger>
        </TabsList>

        {["tukang_taman", "petugas_sampah"].map((j) => (
          <TabsContent value={j} key={j} className="space-y-6">
            {/* Petugas List */}
            <Card><CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-slate-800">Identitas Petugas ({petugas.length})</h3>
                {canEdit && <Button className="brand-btn" size="sm" onClick={() => { setPEditing(null); setPForm({ ...emptyPetugas, jenis: j }); setPOpen(true); }} data-testid="petugas-add-btn"><Plus className="h-4 w-4 mr-1" />Tambah Petugas</Button>}
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {petugas.length === 0 ? <div className="col-span-full text-center py-10 text-slate-400">Belum ada petugas terdaftar</div> :
                  petugas.map((p) => (
                    <div key={p.id} className="rounded-xl border bg-white p-4 flex gap-3 items-start">
                      {p.foto ? <img src={p.foto} alt={p.nama} className="h-16 w-16 rounded-full object-cover ring-2 ring-emerald-200" /> : <div className="h-16 w-16 rounded-full bg-slate-100 grid place-items-center"><UserIcon className="h-8 w-8 text-slate-400" /></div>}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-bold text-slate-800 truncate">{p.nama}</div>
                          {!p.active && <Badge variant="secondary" className="bg-slate-100 text-slate-500">Nonaktif</Badge>}
                        </div>
                        {p.kontak && <div className="text-xs text-slate-500 font-mono">{p.kontak}</div>}
                        {p.jadwal && <div className="text-xs text-emerald-700 mt-1"><CalendarDays className="h-3 w-3 inline mr-1" />{p.jadwal}</div>}
                        {canEdit && <div className="flex gap-1 mt-2">
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { setPEditing(p); setPForm({ ...emptyPetugas, ...p }); setPOpen(true); }}><Edit3 className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={async () => { if (!window.confirm(`Hapus ${p.nama}?`)) return; await petugasLayananAPI.remove(p.id); toast.success("Dihapus"); load(); }}><X className="h-3.5 w-3.5" /></Button>
                        </div>}
                      </div>
                    </div>
                  ))
                }
              </div>
            </CardContent></Card>

            {/* Jadwal 15 Hari (hanya untuk Tukang Taman) */}
            {j === "tukang_taman" && petugas.map((p) => (
              <JadwalTaman key={`jt-${p.id}`} petugas={p} canEdit={canEdit} onReloadPetugas={load} />
            ))}

            {/* Absensi */}
            <Card><CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-slate-800">Absensi & Foto Bertugas</h3>
                {canEdit && petugas.length > 0 && <Button className="brand-btn" size="sm" onClick={() => { setAForm({ ...emptyAbsen, petugas_id: petugas[0].id }); setAOpen(true); }} data-testid="absen-add-btn"><CheckCircle2 className="h-4 w-4 mr-1" />Catat Absen</Button>}
              </div>
              <div className="space-y-3">
                {absensi.length === 0 ? <div className="text-center py-8 text-slate-400 text-sm">Belum ada catatan absensi</div> :
                  absensi.map((a) => (
                    <div key={a.id} className="rounded-xl border bg-white p-3 flex gap-3">
                      {a.petugas_foto && <img src={a.petugas_foto} className="h-12 w-12 rounded-full object-cover shrink-0" alt="" />}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-800">{a.petugas_nama}</span>
                          <Badge className={a.status === "hadir" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : a.status === "izin" ? "bg-amber-100 text-amber-800 hover:bg-amber-100" : "bg-red-100 text-red-700 hover:bg-red-100"}>{a.status.replace("_", " ")}</Badge>
                          <span className="text-xs text-slate-500">{a.tanggal} {a.jam}</span>
                        </div>
                        {a.catatan && <div className="text-xs text-slate-600 mt-1">{a.catatan}</div>}
                        {a.foto?.length > 0 && (
                          <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
                            {a.foto.map((f, i) => <img key={i} src={f} className="h-16 w-16 rounded-md object-cover border shrink-0" alt="" />)}
                          </div>
                        )}
                      </div>
                      {canEdit && <Button size="sm" variant="ghost" className="text-emerald-700 h-8 w-8 p-0" title="Buat Laporan Kegiatan" onClick={() => setLapPrefill({ tanggal: a.tanggal, kategori: jenis, petugas_nama: a.petugas_nama, jam: a.jam })} data-testid={`laporan-btn-absen-${a.id}`}><ClipboardList className="h-4 w-4" /></Button>}
                      {canEdit && <Button size="sm" variant="ghost" className="text-red-600 h-8 w-8 p-0" onClick={async () => { if (!window.confirm("Hapus absensi ini?")) return; await absensiLayananAPI.remove(a.id); toast.success("Dihapus"); load(); }}><X className="h-4 w-4" /></Button>}
                    </div>
                  ))
                }
              </div>
            </CardContent></Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Petugas dialog */}
      <Dialog open={pOpen} onOpenChange={setPOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{pEditing ? "Edit Petugas" : "Tambah Petugas"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              {pForm.foto ? <img src={pForm.foto} alt="" className="h-20 w-20 rounded-full object-cover border" /> : <div className="h-20 w-20 rounded-full bg-slate-100 grid place-items-center"><UserIcon className="h-10 w-10 text-slate-400" /></div>}
              <div>
                <input type="file" accept="image/*" id="petugas-foto" className="hidden" onChange={uploadPetugasFoto} disabled={uploading} />
                <Label htmlFor="petugas-foto" className={`cursor-pointer inline-flex items-center gap-1 px-3 py-2 rounded brand-btn text-white text-sm ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
                  {uploading ? <><Loader2 className="h-3 w-3 animate-spin" />Mengunggah...</> : <><ImagePlus className="h-3.5 w-3.5" />Foto Profil</>}
                </Label>
              </div>
            </div>
            <div><Label>Nama *</Label><Input value={pForm.nama} onChange={(e) => setPForm({ ...pForm, nama: e.target.value })} /></div>
            <div><Label>Kontak (HP/WA)</Label><Input value={pForm.kontak} onChange={(e) => setPForm({ ...pForm, kontak: e.target.value })} /></div>
            <div><Label>Jadwal Bertugas</Label><Input value={pForm.jadwal} onChange={(e) => setPForm({ ...pForm, jadwal: e.target.value })} placeholder="Senin & Kamis, 07:00-10:00" /></div>
            <div><Label>Catatan</Label><Textarea rows={2} value={pForm.catatan} onChange={(e) => setPForm({ ...pForm, catatan: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={pForm.active} onChange={(e) => setPForm({ ...pForm, active: e.target.checked })} />Aktif</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPOpen(false)}>Batal</Button>
            <Button className="brand-btn" onClick={savePetugas} data-testid="petugas-save-btn">{pEditing ? "Simpan" : "Tambah"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Absen dialog */}
      <Dialog open={aOpen} onOpenChange={setAOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Catat Absensi</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Petugas *</Label>
              <Select value={aForm.petugas_id} onValueChange={(v) => setAForm({ ...aForm, petugas_id: v, jadwal_id: "" })}>
                <SelectTrigger><SelectValue placeholder="Pilih petugas..." /></SelectTrigger>
                <SelectContent>{petugas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {/* Link ke hari jadwal (khusus tukang taman) */}
            {jenis === "tukang_taman" && aForm.petugas_id && (jadwalTamanByPetugas[aForm.petugas_id]?.length > 0) && (
              <div><Label>Absen untuk Hari Ke- (opsional)</Label>
                <Select value={aForm.jadwal_id || "none"} onValueChange={(v) => {
                  const jd = jadwalTamanByPetugas[aForm.petugas_id]?.find((x) => x.id === v);
                  setAForm({ ...aForm, jadwal_id: v === "none" ? "" : v, tanggal: jd?.tanggal || aForm.tanggal, jam: jd?.jam || aForm.jam });
                }}>
                  <SelectTrigger data-testid="absen-jadwal-select"><SelectValue placeholder="Pilih hari dari jadwal..." /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="none">— Tidak dikaitkan —</SelectItem>
                    {jadwalTamanByPetugas[aForm.petugas_id].map((jd) => (
                      <SelectItem key={jd.id} value={jd.id}>
                        Hari {jd.hari_ke} • {jd.tanggal} • {jd.area}{jd.is_lembur ? " (LEMBUR)" : ""}{jd.status === "selesai" ? " ✓" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-emerald-700 mt-1">💡 Memilih hari akan otomatis menandai jadwal jadi "Selesai" dan mengisi tanggal & jam.</div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Tanggal *</Label><Input type="date" value={aForm.tanggal} onChange={(e) => setAForm({ ...aForm, tanggal: e.target.value })} /></div>
              <div><Label>Jam</Label><Input type="time" value={aForm.jam} onChange={(e) => setAForm({ ...aForm, jam: e.target.value })} /></div>
            </div>
            <div><Label>Status</Label>
              <Select value={aForm.status} onValueChange={(v) => setAForm({ ...aForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hadir">Hadir</SelectItem>
                  <SelectItem value="izin">Izin</SelectItem>
                  <SelectItem value="tidak_hadir">Tidak Hadir</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Foto Bertugas (multi)</Label>
              <div className="grid grid-cols-4 gap-2 mt-1">
                {(aForm.foto || []).map((f, i) => (
                  <div key={i} className="relative aspect-square rounded-md overflow-hidden border group">
                    <img src={f} className="w-full h-full object-cover" alt="" />
                    <button onClick={() => setAForm({ ...aForm, foto: aForm.foto.filter((_, j) => j !== i) })} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-red-600 text-white grid place-items-center opacity-0 group-hover:opacity-100"><X className="h-3 w-3" /></button>
                  </div>
                ))}
                <label className={`aspect-square rounded-md border-2 border-dashed border-emerald-300 hover:bg-emerald-50 grid place-items-center cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={uploadAbsenFoto} disabled={uploading} data-testid="absen-foto-upload" />
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin text-emerald-600" /> : <Camera className="h-5 w-5 text-emerald-600" />}
                </label>
              </div>
            </div>
            <div><Label>Catatan</Label><Textarea rows={2} value={aForm.catatan} onChange={(e) => setAForm({ ...aForm, catatan: e.target.value })} placeholder="Kegiatan yang dilakukan..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAOpen(false)}>Batal</Button>
            <Button className="brand-btn" onClick={saveAbsen} data-testid="absen-save-btn">Simpan Absen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LaporanKegiatanDialog open={!!lapPrefill} onOpenChange={() => setLapPrefill(null)} prefill={lapPrefill} />
    </div>
  );
};
export default LayananLingkungan;
